import { useState, useMemo } from "react";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, addMonths, parseISO, differenceInCalendarDays } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { STATUS_META } from "../ui-bits";
import CalendarWeekRow from "./CalendarWeekRow";

export default function CalendarView({ tasks, onOpenTask, onAddTask }) {
  const [month, setMonth] = useState(new Date());

  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    const out = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  }, [month]);

  // Tasks with a date range: start_date → due_date (single-day if only one set)
  const timelineTasks = useMemo(() =>
    tasks
      .filter((t) => t.due_date || t.start_date)
      .map((t) => ({ ...t, _start: t.start_date || t.due_date, _end: t.due_date || t.start_date }))
      .filter((t) => t._start <= t._end)
      .sort((a, b) => a._start.localeCompare(b._start) || b._end.localeCompare(a._end)),
    [tasks]);

  const weekBars = useMemo(() => weeks.map((week) => {
    const wStart = format(week[0], "yyyy-MM-dd");
    const wEnd = format(week[6], "yyyy-MM-dd");
    const overlapping = timelineTasks.filter((t) => t._start <= wEnd && t._end >= wStart);
    const laneEnds = [];
    const bars = overlapping.map((t) => {
      const segStart = t._start < wStart ? wStart : t._start;
      const segEnd = t._end > wEnd ? wEnd : t._end;
      const startIdx = differenceInCalendarDays(parseISO(segStart), week[0]);
      const span = differenceInCalendarDays(parseISO(segEnd), parseISO(segStart)) + 1;
      let lane = laneEnds.findIndex((end) => end < segStart);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(segEnd); } else { laneEnds[lane] = segEnd; }
      return { task: t, startIdx, span, lane, isStart: t._start === segStart, isEnd: t._end === segEnd };
    });
    const visible = bars.filter((b) => b.lane < 3);
    const hiddenCount = bars.length - visible.length;
    return { bars: visible, hiddenCount, laneCount: laneEnds.length };
  }), [weeks, timelineTasks]);

  return (
    <div data-testid="calendar-view" className="bg-background">
      <div className="flex items-center gap-2 mb-3">
        <button data-testid="calendar-prev" onClick={() => setMonth((m) => addMonths(m, -1))}
          className="h-7 w-7 flex items-center justify-center rounded-md border border-border hover:bg-muted transition-colors"><ChevronLeft size={15} /></button>
        <button data-testid="calendar-today" onClick={() => setMonth(new Date())}
          className="h-7 px-3 text-xs font-medium rounded-md border border-border hover:bg-muted transition-colors">Today</button>
        <button data-testid="calendar-next" onClick={() => setMonth((m) => addMonths(m, 1))}
          className="h-7 w-7 flex items-center justify-center rounded-md border border-border hover:bg-muted transition-colors"><ChevronRight size={15} /></button>
        <h2 className="font-heading text-lg font-bold ml-2" data-testid="calendar-month-label">{format(month, "MMMM yyyy")}</h2>
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <span key={k} className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ backgroundColor: m.color }} />{m.label}</span>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <div className="grid grid-cols-7 bg-surface border-b border-border">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5 border-r border-border last:border-r-0">{d}</div>
          ))}
        </div>

        {weeks.map((week, wi) => {
          const weekKey = format(week[0], "yyyy-MM-dd");
          const { bars, hiddenCount, laneCount } = weekBars[wi];
          return (
            <CalendarWeekRow key={weekKey} week={week} weekKey={weekKey} bars={bars} hiddenCount={hiddenCount}
              laneCount={laneCount} month={month} onOpenTask={onOpenTask} onAddTask={onAddTask} />
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground mt-2">Bars span from each task's start date to its due date. Click a bar to open the task.</p>
    </div>
  );
}
